import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '@/app/contexts/LanguageContext';

export interface BorderAreaConfig {
  enableHeaderBorder: boolean;
  headerBorderColor: string;
  headerBorderWidth: number;
  
  enableDataBorder: boolean;
  dataBorderColor: string;
  dataBorderWidth: number;
  
  enableInnerGrid: boolean;
  innerGridColor: string;
  innerGridWidth: number;
  
  enableZebraStripes: boolean;
}

interface BorderAreasDialogProps {
  onApply: (config: BorderAreaConfig) => void;
  onCancel: () => void;
}

export function BorderAreasDialog({ onApply, onCancel }: BorderAreasDialogProps) {
  const { t } = useLanguage();
  const [config, setConfig] = useState<BorderAreaConfig>({
    enableHeaderBorder: true,
    headerBorderColor: '#000000',
    headerBorderWidth: 3,
    
    enableDataBorder: true,
    dataBorderColor: '#000000',
    dataBorderWidth: 2,
    
    enableInnerGrid: true,
    innerGridColor: '#cccccc',
    innerGridWidth: 1,
    
    enableZebraStripes: false,
  });

  return (
    <div 
      className="fixed inset-0 flex items-center justify-center z-50 p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onCancel}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-[500px] max-h-[80vh] overflow-auto"
        style={{
          backgroundColor: 'var(--md-surface-container-high)',
          color: 'var(--md-on-surface)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <h2 className="text-lg font-semibold">{t('dialogs.borderAreas.title')}</h2>
          <button
            onClick={onCancel}
            className="p-1 rounded hover:opacity-70 transition-opacity"
            style={{ color: 'var(--md-on-surface)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Info Box */}
          <div 
            className="p-4 rounded text-sm"
            style={{
              backgroundColor: 'var(--md-primary-container)',
              color: 'var(--md-on-primary-container)',
            }}
          >
            {t('dialogs.borderAreas.hint')}
          </div>

          {/* Header Border Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableHeaderBorder"
                checked={config.enableHeaderBorder}
                onChange={(e) => setConfig({ ...config, enableHeaderBorder: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enableHeaderBorder" className="font-medium">
                {t('dialogs.borderAreas.headerBorder')}
              </label>
            </div>
            
            {config.enableHeaderBorder && (
              <div className="ml-7 space-y-3">
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.color')}</label>
                  <input
                    type="color"
                    value={config.headerBorderColor}
                    onChange={(e) => setConfig({ ...config, headerBorderColor: e.target.value })}
                    className="w-16 h-8 rounded border"
                    style={{ borderColor: 'var(--md-outline)' }}
                  />
                  <span className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {config.headerBorderColor}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.width')}</label>
                  <select
                    value={config.headerBorderWidth}
                    onChange={(e) => setConfig({ ...config, headerBorderWidth: Number(e.target.value) })}
                    className="px-3 py-1.5 rounded border"
                    style={{
                      backgroundColor: 'var(--md-surface)',
                      borderColor: 'var(--md-outline)',
                      color: 'var(--md-on-surface)',
                    }}
                  >
                    <option value={1}>{t('dialogs.borderAreas.thin')}</option>
                    <option value={2}>{t('dialogs.borderAreas.standard')}</option>
                    <option value={3}>{t('dialogs.borderAreas.medium')} ⭐</option>
                    <option value={4}>{t('dialogs.borderAreas.thick')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

          {/* Data Border Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableDataBorder"
                checked={config.enableDataBorder}
                onChange={(e) => setConfig({ ...config, enableDataBorder: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enableDataBorder" className="font-medium">
                {t('dialogs.borderAreas.dataBorder')}
              </label>
            </div>
            
            {config.enableDataBorder && (
              <div className="ml-7 space-y-3">
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.color')}</label>
                  <input
                    type="color"
                    value={config.dataBorderColor}
                    onChange={(e) => setConfig({ ...config, dataBorderColor: e.target.value })}
                    className="w-16 h-8 rounded border"
                    style={{ borderColor: 'var(--md-outline)' }}
                  />
                  <span className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {config.dataBorderColor}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.width')}</label>
                  <select
                    value={config.dataBorderWidth}
                    onChange={(e) => setConfig({ ...config, dataBorderWidth: Number(e.target.value) })}
                    className="px-3 py-1.5 rounded border"
                    style={{
                      backgroundColor: 'var(--md-surface)',
                      borderColor: 'var(--md-outline)',
                      color: 'var(--md-on-surface)',
                    }}
                  >
                    <option value={1}>{t('dialogs.borderAreas.thin')}</option>
                    <option value={2}>{t('dialogs.borderAreas.standard')} ⭐</option>
                    <option value={3}>{t('dialogs.borderAreas.medium')}</option>
                    <option value={4}>{t('dialogs.borderAreas.thick')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

          {/* Inner Grid Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableInnerGrid"
                checked={config.enableInnerGrid}
                onChange={(e) => setConfig({ ...config, enableInnerGrid: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enableInnerGrid" className="font-medium">
                {t('dialogs.borderAreas.innerGrid')}
              </label>
            </div>
            
            {config.enableInnerGrid && (
              <div className="ml-7 space-y-3">
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.color')}</label>
                  <input
                    type="color"
                    value={config.innerGridColor}
                    onChange={(e) => setConfig({ ...config, innerGridColor: e.target.value })}
                    className="w-16 h-8 rounded border"
                    style={{ borderColor: 'var(--md-outline)' }}
                  />
                  <span className="text-sm" style={{ color: 'var(--md-on-surface-variant)' }}>
                    {config.innerGridColor}
                  </span>
                </div>
                
                <div className="flex items-center gap-4">
                  <label className="text-sm w-20">{t('dialogs.borderAreas.width')}</label>
                  <select
                    value={config.innerGridWidth}
                    onChange={(e) => setConfig({ ...config, innerGridWidth: Number(e.target.value) })}
                    className="px-3 py-1.5 rounded border"
                    style={{
                      backgroundColor: 'var(--md-surface)',
                      borderColor: 'var(--md-outline)',
                      color: 'var(--md-on-surface)',
                    }}
                  >
                    <option value={1}>{t('dialogs.borderAreas.thin')} ⭐</option>
                    <option value={2}>{t('dialogs.borderAreas.standard')}</option>
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-px" style={{ backgroundColor: 'var(--md-outline-variant)' }} />

          {/* Zebra Stripes Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="enableZebraStripes"
                checked={config.enableZebraStripes}
                onChange={(e) => setConfig({ ...config, enableZebraStripes: e.target.checked })}
                className="w-4 h-4"
              />
              <label htmlFor="enableZebraStripes" className="font-medium">
                {t('dialogs.borderAreas.zebraStripes')}
              </label>
            </div>
            
            {config.enableZebraStripes && (
              <div 
                className="ml-7 p-3 rounded text-sm"
                style={{
                  backgroundColor: 'var(--md-surface-container-low)',
                  color: 'var(--md-on-surface-variant)',
                }}
              >
                {t('dialogs.borderAreas.zebraStripesDesc')}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div 
          className="flex justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: 'var(--md-outline)' }}
        >
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded transition-colors hover:opacity-80"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--md-on-surface)',
              border: '1px solid var(--md-outline)',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={() => onApply(config)}
            className="px-4 py-2 rounded font-medium transition-colors hover:opacity-90"
            style={{
              backgroundColor: 'var(--md-primary)',
              color: 'var(--md-on-primary)',
            }}
          >
            {t('dialogs.borderAreas.apply')}
          </button>
        </div>
      </div>
    </div>
  );
}
